/**
 * One assignment rule: when (assigned to anyone / chosen people / chosen departments; of which
 * types; from where; single or bulk) and then — in order — run a workflow for the contact,
 * notify people, add / remove the contact's tags, set a deadline, and escalate when it is not
 * started (or not done) in time. Below: the rule's recent runs, with what failed and why.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ActivityIndicator, ScrollView, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal } from '../components';
import { Button, Toggle } from '../components/design-system';
import { Chip, StatusBadge } from '../components/lead-finder/leadFinderUi';
import { useRuleOptions, Option } from '../components/assignment-rules/useRuleOptions';
import { apiService, AssignmentRuleBatch, AssignmentRuleInput, AssignmentRuleJobIssue } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { useThemedStyles } from '../hooks/useThemedStyles';
import type { TranslationKey } from '../i18n/en';

const TYPES = ['contact', 'task', 'visit', 'phone_call', 'lead', 'ticket'];
const SOURCES = ['manual', 'bulk', 'flow', 'channel_settings', 'quick_reply', 'webhook', 'contact_add'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

const EMPTY: AssignmentRuleInput = {
  name: '',
  isActive: true,
  trigger: { to: 'anyone', userIds: [], departmentIds: [], assignmentTypes: ['contact'], sources: [], batch: 'any' },
  actions: {
    workflow: { enabled: false, channel: 'whatsapp' },
    notify: { enabled: false, managers: true, userIds: [] },
    tags: { enabled: false, add: [], remove: [] },
    deadline: { enabled: false },
    escalate: { enabled: false, afterHours: 4, when: 'not_started', notifyManagers: true, notifyUserIds: [] },
  },
};

const toggleIn = <T,>(list: T[] = [], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const splitTags = (s: string) => s.split(/[,،\n]/).map((x) => x.trim()).filter(Boolean);


// Pieces of the form — outside the screen so their text boxes keep focus while typing.

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  return (
    <View style={styles.card}>
      <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{title}</Text>
      {children}
    </View>
  );
};

const Label: React.FC<{ text: string }> = ({ text }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  return <Text style={[styles.label, { textAlign: rtl.textStart }]}>{text}</Text>;
};

const Pick: React.FC<{ list: Option[]; selected: string[]; onToggle: (id: string) => void; empty: string; loading?: boolean }> = ({ list, selected, onToggle, empty, loading }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  return (
    <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
      {list.length ? list.map((o) => <Chip key={o.id} label={o.label} selected={selected.includes(o.id)} onPress={() => onToggle(o.id)} />)
        : <Text style={styles.meta}>{loading ? '…' : empty}</Text>}
    </View>
  );
};

const ActionCard: React.FC<{ icon: keyof typeof Ionicons.glyphMap; title: string; hint: string; on: boolean; onToggle: (v: boolean) => void; children?: React.ReactNode }> =
  ({ icon, title, hint, on, onToggle, children }) => {
    const styles = useThemedStyles(getStyles);
    const rtl = useRTLStyles();
    return (
      <View style={[styles.action, on && styles.actionOn]}>
        <View style={[styles.actionHead, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name={icon} size={18} color={(on ? colors.primary : colors.text.secondary) as string} />
          <View style={styles.flex}>
            <Text style={[styles.actionTitle, { textAlign: rtl.textStart }]}>{title}</Text>
            <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{hint}</Text>
          </View>
          <Toggle value={on} onValueChange={onToggle} size="sm" />
        </View>
        {on ? <View style={styles.actionBody}>{children}</View> : null}
      </View>
    );
  };

interface Props { ruleId: string }

export const AssignmentRuleEditScreen: React.FC<Props> = ({ ruleId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const goBack = useGoBack('assignment-rules');
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const options = useRuleOptions();
  const isNew = !ruleId || ruleId === 'new';

  const [rule, setRule] = useState<AssignmentRuleInput>(EMPTY);
  const [tagsAdd, setTagsAdd] = useState('');
  const [tagsRemove, setTagsRemove] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [history, setHistory] = useState<AssignmentRuleBatch[]>([]);
  const [issues, setIssues] = useState<Record<string, AssignmentRuleJobIssue[] | 'loading'>>({});

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const [res, hist] = await Promise.all([apiService.getAssignmentRule(ruleId), apiService.getAssignmentRuleHistory(ruleId)]);
        if (res.statusCode === 1000 && res.data) {
          const r = res.data;
          setRule({ name: r.name, isActive: r.isActive, trigger: { ...EMPTY.trigger, ...r.trigger }, actions: {
            workflow: { ...EMPTY.actions.workflow!, ...r.actions?.workflow },
            notify: { ...EMPTY.actions.notify!, ...r.actions?.notify },
            tags: { ...EMPTY.actions.tags!, ...r.actions?.tags },
            deadline: { ...EMPTY.actions.deadline!, ...r.actions?.deadline },
            escalate: { ...EMPTY.actions.escalate!, ...r.actions?.escalate },
          } });
          setTagsAdd((r.actions?.tags?.add || []).join(', '));
          setTagsRemove((r.actions?.tags?.remove || []).join(', '));
        } else {
          showNotification('error', t('assignmentRules.title'), res.errors?.[0]?.message || t('assignmentRules.error.load'));
        }
        if (hist.statusCode === 1000 && hist.data) setHistory(hist.data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew, ruleId, showNotification, t]);

  const setTrigger = (patch: Partial<AssignmentRuleInput['trigger']>) => setRule((r) => ({ ...r, trigger: { ...r.trigger, ...patch } }));
  const setAction = <K extends keyof AssignmentRuleInput['actions']>(key: K, patch: Partial<NonNullable<AssignmentRuleInput['actions'][K]>>) =>
    setRule((r) => ({ ...r, actions: { ...r.actions, [key]: { ...(r.actions[key] as any), ...patch } } }));

  const save = async () => {
    const body: AssignmentRuleInput = { ...rule, actions: { ...rule.actions, tags: { ...rule.actions.tags!, add: splitTags(tagsAdd), remove: splitTags(tagsRemove) } } };
    setSaving(true);
    try {
      const res = isNew ? await apiService.createAssignmentRule(body) : await apiService.updateAssignmentRule(ruleId, body);
      if (res.statusCode === 1000) {
        showNotification('success', t('assignmentRules.title'), t('assignmentRules.saved'));
        navigateTo('assignment-rules');
      } else {
        showNotification('error', t('assignmentRules.title'), res.errors?.[0]?.message || t('assignmentRules.error.save'));
      }
    } catch (err: any) {
      showNotification('error', t('assignmentRules.title'), err?.message || t('assignmentRules.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      const res = await apiService.deleteAssignmentRule(ruleId);
      if (res.statusCode === 1000) navigateTo('assignment-rules');
      else showNotification('error', t('assignmentRules.title'), res.errors?.[0]?.message || t('assignmentRules.error.save'));
    } finally {
      setDeleting(false);
      setShowDelete(false);
    }
  };

  const loadIssues = async (batchId: string) => {
    if (issues[batchId]) { setIssues(({ [batchId]: _, ...rest }) => rest); return; }
    setIssues((m) => ({ ...m, [batchId]: 'loading' }));
    const res = await apiService.getAssignmentRuleBatchIssues(batchId).catch(() => null);
    setIssues((m) => ({ ...m, [batchId]: res?.data?.items || [] }));
  };

  // ── Pieces ────────────────────────────────────────────────────────────────

  const input = (value: string, onChange: (v: string) => void, placeholder?: string, extra?: object) => (
    <TextInput value={value} onChangeText={onChange} placeholder={placeholder} placeholderTextColor={colors.text.secondary as string}
      style={[styles.input, { textAlign: rtl.textStart }, extra]} />
  );

  const tr = rule.trigger, a = rule.actions;
  const numbers = options.numbers.filter((n) => n.channel === (a.workflow?.channel || 'whatsapp'));

  const renderWhen = () => (
    <Section title={t('assignmentRules.section.when')}>
      <Label text={t('assignmentRules.field.assignedTo')} />
      <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
        {(['anyone', 'users', 'departments'] as const).map((k) => (
          <Chip key={k} label={t(`assignmentRules.to.${k}` as TranslationKey)} selected={tr.to === k} onPress={() => setTrigger({ to: k })} />
        ))}
      </View>
      {tr.to === 'users' ? <Pick loading={options.loading} list={options.users} selected={tr.userIds || []} onToggle={(id) => setTrigger({ userIds: toggleIn(tr.userIds, id) })} empty={t('assignmentRules.none.users')} /> : null}
      {tr.to === 'departments' ? <Pick loading={options.loading} list={options.departments} selected={tr.departmentIds || []} onToggle={(id) => setTrigger({ departmentIds: toggleIn(tr.departmentIds, id) })} empty={t('assignmentRules.none.departments')} /> : null}

      <Label text={t('assignmentRules.field.types')} />
      <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
        {TYPES.map((k) => <Chip key={k} label={t(`assignmentRules.type.${k}` as TranslationKey)} selected={(tr.assignmentTypes || []).includes(k)} onPress={() => setTrigger({ assignmentTypes: toggleIn(tr.assignmentTypes, k) })} />)}
      </View>
      <Text style={styles.meta}>{t('assignmentRules.hint.anyType')}</Text>

      <Label text={t('assignmentRules.field.batch')} />
      <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
        {(['any', 'single', 'bulk'] as const).map((k) => <Chip key={k} label={t(`assignmentRules.batch.${k}` as TranslationKey)} selected={(tr.batch || 'any') === k} onPress={() => setTrigger({ batch: k })} />)}
      </View>

      <Label text={t('assignmentRules.field.sources')} />
      <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
        {SOURCES.map((k) => <Chip key={k} label={t(`assignmentRules.source.${k}` as TranslationKey)} selected={(tr.sources || []).includes(k)} onPress={() => setTrigger({ sources: toggleIn(tr.sources, k) })} />)}
      </View>
      <Text style={styles.meta}>{t('assignmentRules.hint.anySource')}</Text>
    </Section>
  );

  const renderThen = () => (
    <Section title={t('assignmentRules.section.then')}>
      <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('assignmentRules.hint.bulk')}</Text>

      <ActionCard icon="git-branch-outline" title={t('assignmentRules.action.workflow')} hint={t('assignmentRules.action.workflowHint')}
        on={!!a.workflow?.enabled} onToggle={(v) => setAction('workflow', { enabled: v })}>
        <Label text={t('assignmentRules.field.workflow')} />
        <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
          {options.flows.length ? options.flows.map((f) => (
            <Chip key={f.id} label={f.published ? f.label : `${f.label} · ${t('assignmentRules.flow.unpublished')}`} disabled={!f.published}
              selected={a.workflow?.flowId === f.id} onPress={() => setAction('workflow', { flowId: f.id })} />
          )) : <Text style={styles.meta}>{options.loading ? '…' : t('assignmentRules.none.flows')}</Text>}
        </View>
        <Label text={t('assignmentRules.field.channel')} />
        <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
          {(['whatsapp', 'facebook_messenger'] as const).map((c) => (
            <Chip key={c} icon={c === 'whatsapp' ? 'logo-whatsapp' : 'logo-facebook'} label={t(`assignmentRules.channel.${c}` as TranslationKey)}
              selected={(a.workflow?.channel || 'whatsapp') === c} onPress={() => setAction('workflow', { channel: c, channelIdentifier: undefined })} />
          ))}
        </View>
        {numbers.length > 1 ? (
          <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
            {numbers.map((n) => <Chip key={n.id} label={n.label} selected={a.workflow?.channelIdentifier === n.id} onPress={() => setAction('workflow', { channelIdentifier: n.id })} />)}
          </View>
        ) : null}
        {(a.workflow?.channel || 'whatsapp') === 'whatsapp' ? (
          <View style={[styles.warn, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="information-circle-outline" size={16} color={colors.status.warning as string} />
            <Text style={[styles.warnText, { textAlign: rtl.textStart }]}>{t('assignmentRules.hint.template')}</Text>
          </View>
        ) : null}
      </ActionCard>

      <ActionCard icon="notifications-outline" title={t('assignmentRules.action.notify')} hint={t('assignmentRules.action.notifyHint')}
        on={!!a.notify?.enabled} onToggle={(v) => setAction('notify', { enabled: v })}>
        <Toggle value={!!a.notify?.managers} onValueChange={(v) => setAction('notify', { managers: v })} label={t('assignmentRules.field.managers')} />
        <Label text={t('assignmentRules.field.people')} />
        <Pick loading={options.loading} list={options.users} selected={a.notify?.userIds || []} onToggle={(id) => setAction('notify', { userIds: toggleIn(a.notify?.userIds, id) })} empty={t('assignmentRules.none.users')} />
        <Label text={t('assignmentRules.field.message')} />
        {input(a.notify?.message || '', (v) => setAction('notify', { message: v }), t('assignmentRules.placeholder.message'))}
      </ActionCard>

      <ActionCard icon="pricetags-outline" title={t('assignmentRules.action.tags')} hint={t('assignmentRules.action.tagsHint')}
        on={!!a.tags?.enabled} onToggle={(v) => setAction('tags', { enabled: v })}>
        <Label text={t('assignmentRules.field.tagsAdd')} />
        {input(tagsAdd, setTagsAdd, t('assignmentRules.placeholder.tags'))}
        <Label text={t('assignmentRules.field.tagsRemove')} />
        {input(tagsRemove, setTagsRemove, t('assignmentRules.placeholder.tags'))}
      </ActionCard>

      <ActionCard icon="timer-outline" title={t('assignmentRules.action.deadline')} hint={t('assignmentRules.action.deadlineHint')}
        on={!!a.deadline?.enabled} onToggle={(v) => setAction('deadline', { enabled: v })}>
        <Label text={t('assignmentRules.field.priority')} />
        <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
          {PRIORITIES.map((p) => <Chip key={p} label={t(`assignmentRules.priority.${p}` as TranslationKey)} selected={a.deadline?.priority === p} onPress={() => setAction('deadline', { priority: a.deadline?.priority === p ? undefined : p })} />)}
        </View>
        <Label text={t('assignmentRules.field.dueIn')} />
        {input(a.deadline?.dueInHours ? String(a.deadline.dueInHours) : '', (v) => setAction('deadline', { dueInHours: Number(v.replace(/[^\d.]/g, '')) || undefined }), '24', styles.short)}
      </ActionCard>

      <ActionCard icon="trending-up-outline" title={t('assignmentRules.action.escalate')} hint={t('assignmentRules.action.escalateHint')}
        on={!!a.escalate?.enabled} onToggle={(v) => setAction('escalate', { enabled: v })}>
        <Label text={t('assignmentRules.field.after')} />
        {input(a.escalate?.afterHours ? String(a.escalate.afterHours) : '', (v) => setAction('escalate', { afterHours: Number(v.replace(/[^\d.]/g, '')) || undefined }), '4', styles.short)}
        <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
          {(['not_started', 'not_completed'] as const).map((k) => <Chip key={k} label={t(`assignmentRules.when.${k}` as TranslationKey)} selected={(a.escalate?.when || 'not_started') === k} onPress={() => setAction('escalate', { when: k })} />)}
        </View>
        <Label text={t('assignmentRules.field.reassignUser')} />
        <Pick loading={options.loading} list={options.users} selected={a.escalate?.toUserId ? [a.escalate.toUserId] : []}
          onToggle={(id) => setAction('escalate', { toUserId: a.escalate?.toUserId === id ? undefined : id, toDepartmentId: undefined })} empty={t('assignmentRules.none.users')} />
        <Label text={t('assignmentRules.field.reassignDepartment')} />
        <Pick loading={options.loading} list={options.departments} selected={a.escalate?.toDepartmentId ? [a.escalate.toDepartmentId] : []}
          onToggle={(id) => setAction('escalate', { toDepartmentId: a.escalate?.toDepartmentId === id ? undefined : id, toUserId: undefined })} empty={t('assignmentRules.none.departments')} />
        <Toggle value={!!a.escalate?.notifyManagers} onValueChange={(v) => setAction('escalate', { notifyManagers: v })} label={t('assignmentRules.field.notifyManagers')} />
        <Label text={t('assignmentRules.field.notifyPeople')} />
        <Pick loading={options.loading} list={options.users} selected={a.escalate?.notifyUserIds || []} onToggle={(id) => setAction('escalate', { notifyUserIds: toggleIn(a.escalate?.notifyUserIds, id) })} empty={t('assignmentRules.none.users')} />
      </ActionCard>
    </Section>
  );

  const renderHistory = () => (
    <Section title={t('assignmentRules.section.history')}>
      {history.length ? history.map((b) => {
        const list = issues[b.id];
        return (
          <View key={b.id} style={styles.historyRow}>
            <TouchableOpacity style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]} onPress={() => loadIssues(b.id)}>
              <View style={styles.flex}>
                <Text style={[styles.historyTitle, { textAlign: rtl.textStart }]}>
                  {t(b.kind === 'escalate' ? 'assignmentRules.history.escalate' : 'assignmentRules.history.actions')}{b.bulk ? ` · ${t('assignmentRules.batch.bulk')}` : ''}
                </Text>
                <Text style={styles.meta}>{new Date(b.createdAt).toLocaleString()}</Text>
              </View>
              <Text style={styles.meta}>{t('assignmentRules.history.counts', { done: b.done, total: b.total })}</Text>
              {b.failed ? <StatusBadge label={t('assignmentRules.stats.failed', { count: b.failed })} color={colors.status.error as string} /> : null}
              {b.skipped ? <StatusBadge label={t('assignmentRules.history.skipped', { count: b.skipped })} color={colors.text.secondary as string} /> : null}
            </TouchableOpacity>
            {list === 'loading' ? <ActivityIndicator color={colors.primary} /> : list ? list.map((j) => (
              <Text key={j.id} style={[styles.issue, j.status === 'failed' && styles.issueFailed, { textAlign: rtl.textStart }]}>• {[j.error, ...j.notes].filter(Boolean).join(' · ') || j.status}</Text>
            )) : null}
          </View>
        );
      }) : <Text style={styles.meta}>{t('assignmentRules.history.empty')}</Text>}
    </Section>
  );

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <FormHeader
          title={isNew ? t('assignmentRules.new') : rule.name || t('assignmentRules.title')}
          onBack={goBack}
          actions={[
            { icon: saving ? 'hourglass-outline' : 'checkmark', primary: true, onPress: save, label: t('assignmentRules.save') },
            ...(!isNew ? [{ icon: 'trash-outline' as const, onPress: () => setShowDelete(true), label: t('assignmentRules.delete') }] : []),
          ]}
        />
      }
    >
      <View style={styles.container}>
        {loading ? <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View> : (
          <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
            <View style={styles.card}>
              <Label text={t('assignmentRules.field.name')} />
              {input(rule.name, (v) => setRule((r) => ({ ...r, name: v })), t('assignmentRules.placeholder.name'))}
              <Toggle value={rule.isActive !== false} onValueChange={(v) => setRule((r) => ({ ...r, isActive: v }))} label={t('assignmentRules.field.active')} />
            </View>
            {renderWhen()}
            {renderThen()}
            {!isNew ? renderHistory() : null}
            <Button title={t('assignmentRules.save')} onPress={save} variant="primary" loading={saving} disabled={saving} />
          </ScrollView>
        )}
      </View>
      <ConfirmationModal
        visible={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={remove}
        title={t('assignmentRules.deleteTitle')}
        message={t('assignmentRules.deleteMessage')}
        confirmText={t('assignmentRules.delete')}
        cancelText={t('common.cancel')}
        type="error"
        loading={deleting}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  content: { padding: spacing.md, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl, gap: spacing.md, maxWidth: 960, width: '100%', alignSelf: 'center' },
  card: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, padding: spacing.md, gap: spacing.sm, ...designTokens.shadows.sm },
  sectionTitle: { fontSize: typography.fontSize.lg, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  label: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold, marginTop: spacing.xs },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  wrap: { flexWrap: 'wrap', gap: spacing.xs },
  flex: { flex: 1 },
  rowBetween: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  input: {
    borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular, backgroundColor: colors.background.primary,
  },
  short: { maxWidth: 140 },
  action: { borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, padding: spacing.sm, gap: spacing.sm },
  actionOn: { borderColor: colors.primary },
  actionHead: { alignItems: 'center', gap: spacing.sm },
  actionTitle: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  actionBody: { gap: spacing.xs },
  warn: { alignItems: 'flex-start', gap: spacing.xs, padding: spacing.sm, borderRadius: designTokens.borderRadius.sm, backgroundColor: String(colors.status.warning) + '18' },
  warnText: { flex: 1, fontSize: typography.fontSize.xs, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  historyRow: { gap: spacing.xs, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  historyTitle: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  issueFailed: { color: colors.error },
  issue: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xxl },
});
